import { Check, X } from "lucide-react";

import { cn } from "@/utils";
import {
  evaluatePassword,
  getPasswordStrength,
  hasWhitespace,
  type PasswordStrengthLevel,
} from "@/utils/password-policy";

interface PasswordRequirementsProps {
  password: string;
  className?: string;
  /** Show the strength meter above the checklist. Defaults to true. */
  showStrength?: boolean;
}

type StrengthStyle = { bar: string; text: string; segments: number };

const STRENGTH_STYLES: Record<Exclude<PasswordStrengthLevel, "empty">, StrengthStyle> = {
  weak: { bar: "bg-red-500", text: "text-red-500", segments: 1 },
  fair: { bar: "bg-amber-500", text: "text-amber-500", segments: 2 },
  good: { bar: "bg-blue-500", text: "text-blue-500", segments: 3 },
  strong: { bar: "bg-emerald-500", text: "text-emerald-500", segments: 4 },
};

/**
 * Live password-policy feedback: a strength meter plus a per-rule checklist that
 * turns green as each backend requirement is met. Driven entirely by
 * `password-policy.ts`, which mirrors the API's PASSWORD_STRONG rule.
 */
export function PasswordRequirements({
  password,
  className,
  showStrength = true,
}: PasswordRequirementsProps) {
  const checks = evaluatePassword(password);
  const strength = getPasswordStrength(password);
  const style = strength.level === "empty" ? null : STRENGTH_STYLES[strength.level];

  return (
    <div className={cn("space-y-3", className)}>
      {showStrength && style && (
        <div className="space-y-1.5">
          <div className="flex gap-1" aria-hidden="true">
            {[1, 2, 3, 4].map((segment) => (
              <span
                key={segment}
                className={cn(
                  "h-1.5 flex-1 rounded-full transition-colors duration-300",
                  segment <= style.segments ? style.bar : "bg-muted"
                )}
              />
            ))}
          </div>
          <p className={cn("text-[11px] font-semibold", style.text)}>
            {strength.label} password
          </p>
        </div>
      )}

      <ul className="space-y-1.5">
        {checks.map((check) => (
          <li
            key={check.id}
            className={cn(
              "flex items-center gap-2 text-xs transition-colors duration-200",
              check.met
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-muted-foreground"
            )}
          >
            <span
              className={cn(
                "flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-colors duration-200",
                check.met ? "bg-emerald-500/15" : "bg-muted"
              )}
              aria-hidden="true"
            >
              {check.met ? (
                <Check className="h-3 w-3" />
              ) : (
                <X className="h-3 w-3 opacity-50" />
              )}
            </span>
            <span>{check.label}</span>
            <span className="sr-only">{check.met ? "met" : "not met"}</span>
          </li>
        ))}
      </ul>

      {hasWhitespace(password) && (
        <p className="flex items-center gap-1.5 text-xs font-medium text-destructive">
          <X className="h-3 w-3 shrink-0" aria-hidden="true" />
          Spaces aren&apos;t allowed
        </p>
      )}
    </div>
  );
}
